import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Copy, FileUp, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useCachedList, primeCache } from "@/lib/cache";
import { useEditGate } from "@/components/EditGate";
import TierFilter, { TierBadge } from "@/components/TierFilter";
import { downloadTemplate, downloadImportReport, importToastMessage } from "@/lib/importReport";
import { TagPicker } from "@/components/tags/TagPicker";
import { matchesTags } from "@/components/tags/TagBadges";
import { InlineTags } from "@/components/tags/InlineTags";

const DIR_TIERS = ["L1.5", "L2", "L3"];

export default function RecipeDirectoryPage() {
  const [recipesData, refreshRecipes] = useCachedList("recipes");
  const recipes = recipesData || [];
  const loading = !recipesData;
  const [query, setQuery] = useState("");
  const [tagFilter, setTagFilter] = useState([]);
  const [tierFilter, setTierFilter] = useState(() => new Set(DIR_TIERS));
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(null); // {done, total, name}
  const { requireEdit } = useEditGate();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const importFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (importing) { toast.error("A bulk upload is already in progress — please wait"); return; }
    requireEdit(async () => {
      setImporting(true);
      setProgress({ done: 0, total: 0, name: "" });
      const fd = new FormData();
      fd.append("file", file);
      const base = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("token");
      try {
        const resp = await fetch(`${base}/api/recipes/import-cooked`, {
          method: "POST", body: fd,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) {
          const t = await resp.text();
          throw new Error(t.slice(0, 300) || `HTTP ${resp.status}`);
        }
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        let final = null;
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let idx;
          while ((idx = buf.indexOf("\n")) >= 0) {
            const line = buf.slice(0, idx).trim(); buf = buf.slice(idx + 1);
            if (!line) continue;
            let msg; try { msg = JSON.parse(line); } catch { continue; }
            if (msg.phase === "start") setProgress({ done: 0, total: msg.total, name: "" });
            else if (msg.phase === "row") setProgress({ done: msg.done, total: msg.total, name: msg.name || "" });
            else if (msg.phase === "aborted") setProgress({ done: msg.done, total: msg.total, name: "aborted" });
            else if (msg.phase === "result") final = msg;
          }
        }
        if (!final) throw new Error("Upload was interrupted before completion — no data was saved for un-processed rows.");
        primeCache("recipes", final.rows);
        toast.success(`Import done: ${importToastMessage(final)}`, { duration: 10000 });
        if (final.errors?.length) final.errors.slice(0, 8).forEach((m) => toast.error(m, { duration: 10000 }));
        downloadImportReport("cooked_recipes", final);
        refreshRecipes();
      } catch (err) { toast.error(err?.message || apiError(err), { duration: 10000 }); }
      finally { setImporting(false); setProgress(null); }
    });
  };

  const rows = useMemo(() => {
    let r = recipes.filter((x) => DIR_TIERS.includes(x.level) && matchesTags(x, tagFilter));
    if (tierFilter.size > 0) r = r.filter((x) => tierFilter.has(x.level));
    else r = [];
    if (query.trim()) r = r.filter((x) => `${x.name} ${x.sku || ""}`.toLowerCase().includes(query.toLowerCase()));
    return r.sort((a, b) => a.level.localeCompare(b.level) || a.name.localeCompare(b.name));
  }, [recipes, query, tierFilter, tagFilter]);

  const copyAll = () => {
    const header = ["SKU", "Name", "Tier", "Output", "Yield %", "Batch Cost", "EP Cost/Unit", "Portion Cost", "Tags"].join("\t");
    const body = rows.map((r) => [r.sku || "", r.name, r.level, `${r.output_qty} ${r.output_unit}`, r.yield_pct.toFixed(1), r.total_cost.toFixed(2), r.unit_cost.toFixed(4), r.portion_cost.toFixed(2), (r.tags || []).join(", ")].join("\t")).join("\n");
    navigator.clipboard.writeText(`${header}\n${body}`);
    toast.success("Copied to clipboard (TSV)");
  };

  return (
    <div className="p-6 md:p-8" data-testid="recipe-directory-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Recipe Directory</h1>
          <p className="mt-1 text-sm text-zinc-500">Engineered recipes, yields and portion costs.</p>
        </div>
        <div className="flex flex-wrap gap-2 no-print">
          <input ref={fileRef} data-testid="import-cooked-file-input" type="file" accept=".xlsx,.csv" className="hidden" onChange={importFile} />
          <button data-testid="download-cooked-template-button" onClick={() => downloadTemplate("cooked_recipes_template.csv",
            ["SKU (optional)", "Recipe name", "Tier", "Ingredient name", "Ingredient amount (g)", "Post-cook weight (g)", "Unit (optional)", "Portion size (optional)"],
            [
              ["", "Caramelised Onion", "L1.5", "Sliced Onion", 1000, 900, "g", 0],
              ["", "Caramelised Onion", "L1.5", "Cooking Oil", 50, "", "", ""],
              ["", "Makhani Gravy", "L2", "Caramelised Onion", 200, 2200, "g", 0],
              ["", "Makhani Gravy", "L2", "Butter", 100, "", "", ""],
              ["", "Butter Chicken", "L3", "Makhani Gravy", 250, 350, "portion", 350],
            ])}
            className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50">
            <FileUp className="h-4 w-4 rotate-180" strokeWidth={1.5} /> Template
          </button>
          <button data-testid="import-cooked-button" onClick={() => fileRef.current?.click()} disabled={importing}
            className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : <FileUp className="h-4 w-4" strokeWidth={1.5} />}
            {importing ? "Importing…" : "Import cooked recipes"}
          </button>
          <button data-testid="copy-recipes-button" onClick={copyAll}
            className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50">
            <Copy className="h-4 w-4" strokeWidth={1.5} /> Copy grid
          </button>
          <button data-testid="create-recipe-button" onClick={() => requireEdit(() => navigate("/recipes/new"))}
            className="inline-flex items-center gap-1.5 rounded-sm bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700">
            <Plus className="h-4 w-4" strokeWidth={1.5} /> Create new
          </button>
        </div>
      </div>

      {importing && (
        <div className="mb-3 border border-blue-200 bg-blue-50 px-3 py-2" data-testid="cooked-import-progress-banner">
          <div className="flex items-center gap-3 text-xs text-blue-800">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
            <span className="font-medium" data-testid="cooked-import-progress-text">
              {progress && progress.total > 0
                ? `Processing recipe ${progress.done} of ${progress.total}${progress.name ? ` — ${progress.name}` : ""}`
                : "Preparing file…"}
            </span>
            <span className="text-blue-700/80">Do NOT refresh or close this tab — that will abort the upload.</span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-blue-100">
            <div
              className="h-full rounded-full bg-blue-500 transition-all duration-300"
              style={{ width: progress && progress.total > 0 ? `${Math.min(100, Math.round((progress.done / progress.total) * 100))}%` : "8%" }}
            />
          </div>
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="flex min-w-0 max-w-md flex-1 basis-64 items-center gap-2 rounded-sm border border-zinc-300 bg-white px-3 py-2">
          <Search className="h-4 w-4 text-zinc-400" strokeWidth={1.5} />
          <input data-testid="recipes-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search recipes or SKU…"
            className="w-full border-0 bg-transparent p-0 text-sm focus:outline-none focus:ring-0" />
        </div>
        <TierFilter tiers={DIR_TIERS} selected={tierFilter} onChange={setTierFilter} testidPrefix="dir-tier-filter" />
        <TagPicker testid="directory-tag-filter" filter value={tagFilter} onChange={setTagFilter} />
        <span data-testid="directory-filter-result-count" className="text-xs text-zinc-500">{rows.length} recipes</span>
      </div>

      <div data-testid="directory-table-scroll" tabIndex={0} role="region" aria-label="Recipe directory table" className="overflow-x-auto border border-zinc-300">
        <table data-testid="directory-table" className="w-max min-w-full border-collapse text-left [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap">
          <thead>
            <tr className="bg-zinc-100 text-xs font-semibold uppercase tracking-wider text-zinc-600">
              <th className="border-b border-zinc-300 px-3 py-2 w-28">SKU</th>
              <th className="min-w-64 border-b border-l border-zinc-300 px-3 py-2">Recipe Name</th>
              <th className="border-b border-l border-zinc-300 px-3 py-2 w-20">Tier</th>
              <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-28">Output</th>
              <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-24">Yield %</th>
              <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-32">Batch Cost</th>
              <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-36">EP Cost / Unit</th>
              <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-32">Portion Cost</th>
              <th data-testid="directory-tags-header" className="border-b border-l border-zinc-300 px-3 py-2">Tags</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="px-3 py-8 text-center text-sm text-zinc-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td data-testid="directory-filter-empty" colSpan={9} className="px-3 py-8 text-center text-sm text-zinc-400">{tierFilter.size === 0 ? "No tier selected — pick at least one." : "No recipes found."}</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="cursor-pointer hover:bg-zinc-50 text-[13px]" data-testid={`recipe-dir-row-${r.id}`} onClick={() => navigate(`/recipes/${r.id}`)}>
                <td className="border-b border-zinc-200 px-3 py-2 font-mono text-[12px] text-zinc-600" data-testid={`recipe-dir-sku-${r.id}`}>{r.sku || "—"}</td>
                <td data-testid={`directory-name-${r.id}`} className="border-b border-l border-zinc-200 px-3 py-2 font-medium">
                  <Link data-testid={`directory-recipe-link-${r.id}`} to={`/recipes/${r.id}`} className="hover:text-blue-600" onClick={(e) => e.stopPropagation()}>{r.name}</Link>
                </td>
                <td className="border-b border-l border-zinc-200 px-3 py-2"><TierBadge tier={r.level} /></td>
                <td className="border-b border-l border-zinc-200 px-3 py-2 text-right font-mono">{r.output_qty} {r.output_unit}</td>
                <td className={`border-b border-l border-zinc-200 px-3 py-2 text-right font-mono ${r.yield_pct > 0 && r.yield_pct < 100 ? "text-amber-600" : ""}`}>{r.yield_pct.toFixed(1)}%</td>
                <td className="border-b border-l border-zinc-200 px-3 py-2 text-right font-mono">₹{r.total_cost.toFixed(2)}</td>
                <td className="border-b border-l border-zinc-200 px-3 py-2 text-right font-mono font-medium">₹{r.unit_cost.toFixed(4)}<span className="font-normal text-zinc-400">/{r.output_unit}</span></td>
                <td className="border-b border-l border-zinc-200 px-3 py-2 text-right font-mono">{r.portion_qty > 0 ? `₹${r.portion_cost.toFixed(2)}` : "—"}</td>
                <td className="border-b border-l border-zinc-200 px-3 py-2" onClick={e => e.stopPropagation()}>
                  <InlineTags item={r} testid={`directory-tags-${r.id}`} disabled={importing} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
